// Ctrl+V でクリップボードに画像があれば配置、テキストが URL なら OGP リンクカード化、
// それ以外は無視する。
// 配置位置は最後に追跡したマウス位置（lastPointerStagePosition）。

import { useEffect } from "react";
import { useCanvasStore } from "../store/canvas-store";
import {
  readCanvasObjectsFromClipboardData,
  readCanvasObjectsFromText,
} from "../../../lib/canvas-clipboard";
import type { CanvasObject } from "@refinement-canvas/domain";

interface Handlers {
  readonly onPasteImage: (file: File, position: { x: number; y: number }) => void;
  readonly onPasteUrl: (url: string, position: { x: number; y: number }) => void;
  readonly onPasteCanvasObjects: (objects: readonly CanvasObject[], position: { x: number; y: number }) => void;
  /** 参照専用（埋め込み）。true で貼り付け配置を無効化する。 */
  readonly readOnly?: boolean | undefined;
}

/** コンテキストメニュー等、プログラムから貼り付けを起こす際に渡すハンドラ（readOnly を除いた貼り付け 3 種）。 */
export type ProgrammaticPasteHandlers = Pick<
  Handlers,
  "onPasteImage" | "onPasteUrl" | "onPasteCanvasObjects"
>;

export function usePasteHandler(handlers: Handlers): void {
  const readOnly = handlers.readOnly ?? false;
  useEffect(() => {
    if (readOnly) return undefined;
    const onPaste = (event: ClipboardEvent): void => {
      // 入力要素にフォーカスがある場合は通常のペースト動作に任せる
      if (isEditableTarget(event.target)) return;

      const data = event.clipboardData;
      if (!data) return;

      const position = useCanvasStore.getState().lastPointerStagePosition ?? { x: 0, y: 0 };

      // 1) キャンバスオブジェクトがあれば、別キャンバスでも同じ内容を複製する。
      const canvasObjects = readCanvasObjectsFromClipboardData(data);
      if (canvasObjects.length > 0) {
        event.preventDefault();
        handlers.onPasteCanvasObjects(canvasObjects, position);
        return;
      }

      // 2) 画像があれば優先
      for (const item of Array.from(data.items)) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            event.preventDefault();
            handlers.onPasteImage(file, position);
            return;
          }
        }
      }

      // 3) テキストが URL なら OGP リンクカード化
      const text = data.getData("text/plain");
      if (text && isLikelyUrl(text)) {
        event.preventDefault();
        handlers.onPasteUrl(text.trim(), position);
      }
    };

    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [handlers, readOnly]);
}

function isLikelyUrl(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length > 2048) return false;
  return /^https?:\/\/[^\s]+$/i.test(trimmed);
}

/**
 * コンテキストメニューの「貼り付け」から、非同期 Clipboard API（navigator.clipboard）経由で
 * 貼り付けを実行する。Ctrl+V（paste イベント）と同じ優先順位で、
 * (1) キャンバスオブジェクト → (2) 画像 → (3) URL テキスト の順に振り分ける。
 * 戻り値: "pasted"=貼り付けた / "empty"=対象なし / "unavailable"=API 非対応・権限拒否。
 */
export async function performClipboardPaste(
  handlers: ProgrammaticPasteHandlers,
  position: { x: number; y: number }
): Promise<"pasted" | "empty" | "unavailable"> {
  if (typeof navigator === "undefined" || !navigator.clipboard) return "unavailable";

  // リッチな read()（画像 Blob / テキストの両取得）が使える環境を優先する。
  const read = navigator.clipboard.read?.bind(navigator.clipboard);
  if (read) {
    try {
      const items = await read();
      // 1) キャンバスオブジェクト（text/plain のフォールバック接頭辞）を最優先で復元する。
      for (const item of items) {
        if (!item.types.includes("text/plain")) continue;
        const text = await (await item.getType("text/plain")).text();
        const objects = readCanvasObjectsFromText(text);
        if (objects.length > 0) {
          handlers.onPasteCanvasObjects(objects, position);
          return "pasted";
        }
      }
      // 2) 画像。
      for (const item of items) {
        const imageType = item.types.find((t) => t.startsWith("image/"));
        if (!imageType) continue;
        const blob = await item.getType(imageType);
        const file = new File([blob], `pasted-image.${mimeToExtension(imageType)}`, {
          type: imageType,
        });
        handlers.onPasteImage(file, position);
        return "pasted";
      }
      // 3) URL テキスト。
      for (const item of items) {
        if (!item.types.includes("text/plain")) continue;
        const text = (await (await item.getType("text/plain")).text()).trim();
        if (isLikelyUrl(text)) {
          handlers.onPasteUrl(text, position);
          return "pasted";
        }
      }
      return "empty";
    } catch {
      // read() が拒否 / 失敗した場合は readText() のテキスト経路へフォールバックする。
    }
  }

  // readText() フォールバック（テキスト系のみ。画像は取得できない）。
  try {
    const text = (await navigator.clipboard.readText()).trim();
    if (text.length === 0) return "empty";
    const objects = readCanvasObjectsFromText(text);
    if (objects.length > 0) {
      handlers.onPasteCanvasObjects(objects, position);
      return "pasted";
    }
    if (isLikelyUrl(text)) {
      handlers.onPasteUrl(text, position);
      return "pasted";
    }
    return "empty";
  } catch {
    return "unavailable";
  }
}

/**
 * クリップボードに貼り付け可能な内容（画像・テキスト）があるかを best-effort で判定する。
 * コンテキストメニューの「貼り付け」項目の活性/非活性の初期判定に使う。
 * - "has-content": 読み取れて内容がある → 活性
 * - "empty"      : 読み取れたが空       → 非活性（グレーアウト）
 * - "unknown"    : API 非対応・権限拒否等で判定不可 → 呼び出し側は楽観的に活性化し、
 *                  クリック時の performClipboardPaste の結果（empty ならトースト）に委ねる
 */
export async function probeClipboardPasteable(): Promise<"has-content" | "empty" | "unknown"> {
  if (typeof navigator === "undefined" || !navigator.clipboard) return "unknown";
  const read = navigator.clipboard.read?.bind(navigator.clipboard);
  if (read) {
    try {
      const items = await read();
      for (const item of items) {
        if (item.types.some((t) => t.startsWith("image/"))) return "has-content";
        if (item.types.includes("text/plain")) {
          const text = (await (await item.getType("text/plain")).text()).trim();
          // performClipboardPaste が受け付けるもの（キャンバスオブジェクト or URL）だけを「貼付可」とする。
          // 通常テキストは貼り付け対象外なので、活性化して押下時に「貼り付けるものがありません」になる
          // 食い違いを避けるため empty 扱いにする。
          if (isPasteableText(text)) return "has-content";
        }
      }
      return "empty";
    } catch {
      // fall through to readText()
    }
  }
  try {
    return isPasteableText((await navigator.clipboard.readText()).trim()) ? "has-content" : "empty";
  } catch {
    return "unknown";
  }
}

// text/plain の内容が「貼り付けで実際に処理できる」か（キャンバスオブジェクト payload か URL か）。
function isPasteableText(text: string): boolean {
  if (text.length === 0) return false;
  if (readCanvasObjectsFromText(text).length > 0) return true;
  return isLikelyUrl(text);
}

function mimeToExtension(mimeType: string): string {
  const subtype = mimeType.split("/")[1] ?? "png";
  return subtype === "jpeg" ? "jpg" : subtype;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName.toLowerCase();
  return tag === "input" || tag === "textarea";
}
